import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useTimeTracking } from '../contexts/TimeTrackingContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import CustomerPicker from '../components/CustomerPicker';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { toast } from 'sonner';
import {
    Clock, Plus, Edit, Trash2, CalendarIcon, Lock, ChevronLeft, ChevronRight,
    Play, Pause, AlertCircle, TrendingUp, TrendingDown, Download, FileSpreadsheet, X
} from 'lucide-react';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addWeeks, subWeeks, addMonths, subMonths } from 'date-fns';
import { de } from 'date-fns/locale';

const TimePage = () => {
    const { api, user } = useAuth();
    // Chef/AM: Zeiteintrag fuer einen anderen Mitarbeiter erfassen (z.B. Freelancer ohne Zugang)
    const canManageTeamTime = ['chef', 'admin', 'account_manager'].includes(user?.role);
    const [teamUsers, setTeamUsers] = useState([]);
    const [entryForUserId, setEntryForUserId] = useState('');
    useEffect(() => {
        if (!canManageTeamTime) return;
        api.get('/users').then(r => setTeamUsers((r.data || []).filter(u => u.is_active !== false && u.role !== 'influencer'))).catch(() => {});
    }, [canManageTeamTime]);  // eslint-disable-line react-hooks/exhaustive-deps
    const { isTracking, formattedTime, startTracking, stopTracking, cancelTracking } = useTimeTracking();
    const [entries, setEntries] = useState([]);
    const [stats, setStats] = useState(null);
    const [alltimeBalance, setAlltimeBalance] = useState(null);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState('week');
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [showBalanceModal, setShowBalanceModal] = useState(false);
    const [showStopModal, setShowStopModal] = useState(false);
    const [editingEntry, setEditingEntry] = useState(null);
    const [tasks, setTasks] = useState([]);
    const [projects, setProjects] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [projectsEnabled, setProjectsEnabled] = useState(true);
    const [exportRange, setExportRange] = useState({
        start: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
        end: format(endOfMonth(new Date()), 'yyyy-MM-dd')
    });
    const [exporting, setExporting] = useState(false);
    const [stopForm, setStopForm] = useState({
        customer_id: '',
        task_id: '',
        description: ''
    });
    const [formData, setFormData] = useState({
        task_id: '',
        project_id: '',
        customer_id: '',
        description: '',
        date: format(new Date(), 'yyyy-MM-dd'),
        start_time: '09:00',
        end_time: '17:00',
        is_pause: false
    });

    const getDateRange = () => {
        if (view === 'day') {
            return {
                start: format(selectedDate, 'yyyy-MM-dd') + 'T00:00:00',
                end: format(selectedDate, 'yyyy-MM-dd') + 'T23:59:59'
            };
        } else if (view === 'week') {
            const start = startOfWeek(selectedDate, { weekStartsOn: 1 });
            const end = endOfWeek(selectedDate, { weekStartsOn: 1 });
            return {
                start: format(start, 'yyyy-MM-dd') + 'T00:00:00',
                end: format(end, 'yyyy-MM-dd') + 'T23:59:59'
            };
        } else {
            const start = startOfMonth(selectedDate);
            const end = endOfMonth(selectedDate);
            return {
                start: format(start, 'yyyy-MM-dd') + 'T00:00:00',
                end: format(end, 'yyyy-MM-dd') + 'T23:59:59'
            };
        }
    };

    const fetchData = async () => {
        try {
            const range = getDateRange();
            const [entriesRes, statsRes, tasksRes, projectsRes, customersRes, balanceRes, settingsRes] = await Promise.all([
                api.get('/time-entries', { params: { start_date: range.start, end_date: range.end } }),
                api.get('/time-entries/stats', { params: { start_date: range.start, end_date: range.end } }),
                api.get('/tasks/my'),
                api.get('/projects'),
                api.get('/customers'),
                api.get('/time-entries/alltime-balance'),
                api.get('/system/settings').catch(() => ({ data: {} }))
            ]);
            setEntries(entriesRes.data);
            setStats(statsRes.data);
            setAlltimeBalance(balanceRes.data);
            setTasks(tasksRes.data);
            setProjects(projectsRes.data);
            setCustomers((customersRes.data?.filter(c => !c.is_archived) || []).sort((a, b) => (a.name || '').localeCompare(b.name || '')));
            if (settingsRes.data?.projects_enabled !== undefined) {
                setProjectsEnabled(settingsRes.data.projects_enabled);
            }
        } catch (error) {
            toast.error('Fehler beim Laden der Daten');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [selectedDate, view]);

    const handleSaveEntry = async () => {
        if (!formData.customer_id) {
            toast.error('Bitte waehle einen Kunden aus');
            return;
        }
        try {
            const startDateTime = `${formData.date}T${formData.start_time}:00`;
            const endDateTime = `${formData.date}T${formData.end_time}:00`;

            // Find customer name from customer_id
            const selectedCustomer = customers.find(c => c.id === formData.customer_id);
            const customerName = selectedCustomer?.name || null;

            const payload = {
                task_id: formData.task_id || null,
                project_id: formData.project_id || null,
                customer_id: formData.customer_id || null,
                customer_name: customerName,
                description: formData.description,
                start_time: startDateTime,
                end_time: endDateTime,
                is_pause: formData.is_pause,
                user_id: (canManageTeamTime && entryForUserId) ? entryForUserId : null
            };
            // Ueberschneidung: Server antwortet 409 mit Details -> nachfragen und mit allow_overlap wiederholen
            const postEntry = async (body) => {
                try {
                    await api.post('/time-entries', body);
                } catch (error) {
                    const d = error.response?.data?.detail;
                    if (error.response?.status === 409 && d?.code === 'overlap' && d.can_override) {
                        if (window.confirm(`${d.message}\n\nTrotzdem speichern? (z. B. parallele Leistung oder Nacherfassung)`)) {
                            await api.post('/time-entries', { ...body, allow_overlap: true });
                            return;
                        }
                        throw new Error('Abgebrochen – Eintrag nicht gespeichert');
                    }
                    throw error;
                }
            };

            if (editingEntry) {
                await api.put(`/time-entries/${editingEntry.id}`, {
                    customer_id: formData.customer_id || null,
                    customer_name: customerName,
                    description: formData.description,
                    start_time: startDateTime,
                    end_time: endDateTime
                });
                toast.success('Eintrag aktualisiert');
            } else {
                await postEntry(payload);
                toast.success(entryForUserId ? 'Eintrag für Mitarbeiter erstellt' : 'Eintrag erstellt');
                setEntryForUserId('');
            }

            setShowCreateModal(false);
            setEditingEntry(null);
            resetForm();
            fetchData();
        } catch (error) {
            const d = error.response?.data?.detail;
            toast.error((typeof d === 'object' && d?.message) || (typeof d === 'string' && d) || error.message || 'Fehler beim Speichern');
        }
    };

    const handleDeleteEntry = async (entryId) => {
        if (!window.confirm('Eintrag wirklich löschen?')) return;
        try {
            await api.delete(`/time-entries/${entryId}`);
            toast.success('Eintrag gelöscht');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
        }
    };

    const resetForm = () => {
        setFormData({
            task_id: '',
            project_id: '',
            customer_id: '',
            description: '',
            date: format(new Date(), 'yyyy-MM-dd'),
            start_time: '09:00',
            end_time: '17:00',
            is_pause: false
        });
    };

    const openEditModal = (entry) => {
        setEditingEntry(entry);
        const startDate = new Date(entry.start_time);
        const endDate = new Date(entry.end_time);
        // Find customer_id from customer_name
        const matchingCustomer = customers.find(c => c.name === entry.customer_name);
        setFormData({
            task_id: entry.task_id || '',
            project_id: entry.project_id || '',
            customer_id: matchingCustomer?.id || '',
            description: entry.description,
            date: format(startDate, 'yyyy-MM-dd'),
            start_time: format(startDate, 'HH:mm'),
            end_time: format(endDate, 'HH:mm'),
            is_pause: entry.is_pause
        });
        setShowCreateModal(true);
    };

    const navigateDate = (direction) => {
        if (view === 'day') {
            setSelectedDate(prev => direction === 'next' ? new Date(prev.setDate(prev.getDate() + 1)) : new Date(prev.setDate(prev.getDate() - 1)));
        } else if (view === 'week') {
            setSelectedDate(prev => direction === 'next' ? addWeeks(prev, 1) : subWeeks(prev, 1));
        } else {
            setSelectedDate(prev => direction === 'next' ? addMonths(prev, 1) : subMonths(prev, 1));
        }
    };

    const formatDuration = (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        return `${hours}h ${minutes}m`;
    };

    // Handle stop timer with iOS-style modal
    const handleOpenStopModal = () => {
        setStopForm({ customer_id: '', task_id: '', description: '' });
        setShowStopModal(true);
    };

    const handleStopTimer = async (isPause = false) => {
        if (!isPause && !stopForm.customer_id) {
            toast.error('Bitte waehle einen Kunden aus');
            return;
        }
        try {
            const customer = customers.find(c => c.id === stopForm.customer_id);
            
            await stopTracking(
                customer?.id || null,
                customer?.name || (isPause ? 'Pause' : 'Allgemein'),
                stopForm.description,
                isPause
            );
            
            setShowStopModal(false);
            toast.success(isPause ? 'Pause erfasst' : 'Zeit erfasst');
            fetchData();
        } catch (error) {
            const d = error.response?.data?.detail;
            toast.error((typeof d === 'object' && d?.message) || (typeof d === 'string' && d) || 'Fehler beim Speichern');
        }
    };

    const getDateRangeLabel = () => {
        if (view === 'day') {
            return format(selectedDate, 'EEEE, d. MMMM yyyy', { locale: de });
        } else if (view === 'week') {
            const start = startOfWeek(selectedDate, { weekStartsOn: 1 });
            const end = endOfWeek(selectedDate, { weekStartsOn: 1 });
            return `${format(start, 'd. MMM', { locale: de })} - ${format(end, 'd. MMM yyyy', { locale: de })}`;
        } else {
            return format(selectedDate, 'MMMM yyyy', { locale: de });
        }
    };
    
    // Export time entries
    const handleExport = async () => {
        if (!exportRange.start || !exportRange.end) {
            toast.error('Bitte wähle einen Zeitraum aus.');
            return;
        }
        
        setExporting(true);
        try {
            const response = await api.get('/time-entries/export', {
                params: {
                    start_date: exportRange.start + 'T00:00:00',
                    end_date: exportRange.end + 'T23:59:59',
                    format: 'csv'
                },
                responseType: 'blob'
            });
            
            // Create download link
            const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `zeiteintraege_${exportRange.start}_bis_${exportRange.end}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            toast.success('Export erfolgreich!');
            setShowExportModal(false);
        } catch (error) {
            const message = error.response?.data?.detail || 'Fehler beim Exportieren';
            toast.error(message);
        } finally {
            setExporting(false);
        }
    };

    // Group entries by day for week/month view
    const groupedEntries = entries.reduce((acc, entry) => {
        const day = format(new Date(entry.start_time), 'yyyy-MM-dd');
        if (!acc[day]) acc[day] = [];
        acc[day].push(entry);
        return acc;
    }, {});

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="p-6 md:p-8" data-testid="time-page">
            {/* Header with Timer */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-6">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            ⏱ Zeiterfassung
                        </h1>
                        <p className="text-muted-foreground">Verwalte deine Arbeitszeiten</p>
                    </div>
                    
                    {/* Live Timer Widget */}
                    <Card className={`shadow-soft transition-all ${isTracking ? 'ring-2 ring-primary/50' : ''}`}>
                        <CardContent className="py-3 px-4 flex items-center gap-4">
                            <div>
                                <p className={`text-2xl font-mono font-bold ${isTracking ? 'text-primary' : 'text-muted-foreground'}`}>
                                    {isTracking ? formattedTime : '00:00:00'}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                    {isTracking ? 'Zeit läuft...' : 'Bereit'}
                                </p>
                            </div>
                            <Button
                                size="default"
                                className={`rounded-full w-12 h-12 ${isTracking ? 'bg-destructive hover:bg-destructive/90' : ''}`}
                                onClick={() => isTracking ? handleOpenStopModal() : startTracking()}
                                data-testid="timer-button"
                            >
                                {isTracking ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
                            </Button>
                        </CardContent>
                    </Card>
                </div>
                
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="icon" onClick={() => setShowExportModal(true)} data-testid="export-time-button" title="Export">
                        <Download className="w-4 h-4" />
                    </Button>
                    <Button onClick={() => { resetForm(); setShowCreateModal(true); }} data-testid="add-time-button">
                        <Plus className="w-4 h-4 mr-2" />
                        Zeit hinzufügen
                    </Button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <Card className="shadow-soft" data-testid="stats-total">
                    <CardContent className="pt-6">
                        <div className="text-3xl font-semibold text-primary">{stats?.total_hours || 0}h</div>
                        <p className="text-sm text-muted-foreground">
                            {view === 'day' ? 'Heute' : view === 'week' ? 'Diese Woche' : 'Diesen Monat'}
                        </p>
                    </CardContent>
                </Card>
                <Card className="shadow-soft" data-testid="stats-expected">
                    <CardContent className="pt-6">
                        <div className="text-3xl font-semibold">{stats?.expected_hours || 0}h</div>
                        <p className="text-sm text-muted-foreground">Soll</p>
                    </CardContent>
                </Card>
                <Card className="shadow-soft" data-testid="stats-overtime">
                    <CardContent className="pt-6">
                        <div className={`text-3xl font-semibold flex items-center gap-2 ${stats?.overtime_hours > 0 ? 'text-emerald-500' : ''}`}>
                            {stats?.overtime_hours > 0 && <TrendingUp className="w-5 h-5" />}
                            {stats?.overtime_hours || 0}h
                        </div>
                        <p className="text-sm text-muted-foreground">Überstunden</p>
                    </CardContent>
                </Card>
                <Card className="shadow-soft" data-testid="stats-pause">
                    <CardContent className="pt-6">
                        <div className="text-3xl font-semibold text-muted-foreground">{stats?.pause_hours || 0}h</div>
                        <p className="text-sm text-muted-foreground">Pausen</p>
                    </CardContent>
                </Card>
                {/* Alltime Balance Card */}
                <Card 
                    className="shadow-soft cursor-pointer hover:shadow-md transition-shadow border-2 border-transparent hover:border-primary/20" 
                    data-testid="stats-alltime-balance"
                    onClick={() => setShowBalanceModal(true)}
                >
                    <CardContent className="pt-6">
                        <div className={`text-3xl font-semibold flex items-center gap-2 ${
                            alltimeBalance?.alltime_balance_hours > 0 ? 'text-emerald-500' : 
                            alltimeBalance?.alltime_balance_hours < 0 ? 'text-red-500' : ''
                        }`}>
                            {alltimeBalance?.alltime_balance_hours > 0 && <TrendingUp className="w-5 h-5" />}
                            {alltimeBalance?.alltime_balance_hours < 0 && <TrendingDown className="w-5 h-5" />}
                            {alltimeBalance?.alltime_balance_hours > 0 ? '+' : ''}{alltimeBalance?.alltime_balance_hours || 0}h
                        </div>
                        <p className="text-sm text-muted-foreground">Gesamt-Saldo</p>
                    </CardContent>
                </Card>
            </div>

            {/* Progress */}
            <Card className="shadow-soft mb-6">
                <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium">Fortschritt</span>
                        <span className="text-sm text-muted-foreground">
                            {stats?.total_hours || 0}h / {stats?.expected_hours || 0}h
                        </span>
                    </div>
                    <Progress 
                        value={stats?.expected_hours ? Math.min((stats.total_hours / stats.expected_hours) * 100, 100) : 0} 
                        className="h-3"
                    />
                    {stats?.undertime_hours > 0 && (
                        <p className="text-sm text-destructive mt-2 flex items-center gap-1">
                            <TrendingDown className="w-4 h-4" />
                            {stats.undertime_hours}h Minusstunden
                        </p>
                    )}
                </CardContent>
            </Card>

            {/* View Tabs & Navigation */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6">
                <Tabs value={view} onValueChange={setView} className="w-full md:w-auto">
                    <TabsList>
                        <TabsTrigger value="day" data-testid="view-day">Tag</TabsTrigger>
                        <TabsTrigger value="week" data-testid="view-week">Woche</TabsTrigger>
                        <TabsTrigger value="month" data-testid="view-month">Monat</TabsTrigger>
                    </TabsList>
                </Tabs>

                <div className="flex items-center gap-2">
                    <Button variant="outline" size="icon" onClick={() => navigateDate('prev')} data-testid="nav-prev">
                        <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button variant="outline" className="min-w-[200px]" data-testid="date-picker">
                                <CalendarIcon className="w-4 h-4 mr-2" />
                                {getDateRangeLabel()}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                            <Calendar
                                mode="single"
                                selected={selectedDate}
                                onSelect={(date) => date && setSelectedDate(date)}
                                locale={de}
                            />
                        </PopoverContent>
                    </Popover>
                    <Button variant="outline" size="icon" onClick={() => navigateDate('next')} data-testid="nav-next">
                        <ChevronRight className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" onClick={() => setSelectedDate(new Date())} data-testid="today-button">
                        Heute
                    </Button>
                </div>
            </div>

            {/* Entries List */}
            <Card className="shadow-soft" data-testid="entries-list">
                <CardContent className="p-0">
                    {Object.keys(groupedEntries).length > 0 ? (
                        <div className="divide-y divide-border/50">
                            {Object.entries(groupedEntries)
                                .sort(([a], [b]) => new Date(b) - new Date(a))
                                .map(([day, dayEntries]) => (
                                    <div key={day} className="p-4">
                                        <div className="flex items-center justify-between mb-3">
                                            <h3 className="font-medium">
                                                {format(new Date(day), 'EEEE, d. MMMM', { locale: de })}
                                            </h3>
                                            <span className="text-sm text-muted-foreground">
                                                {formatDuration(dayEntries.filter(e => !e.is_pause).reduce((sum, e) => sum + e.duration_seconds, 0))}
                                            </span>
                                        </div>
                                        <div className="space-y-2">
                                            {dayEntries.map(entry => (
                                                <motion.div
                                                    key={entry.id}
                                                    initial={{ opacity: 0 }}
                                                    animate={{ opacity: 1 }}
                                                    className={`flex items-center justify-between p-3 rounded-xl ${entry.is_pause ? 'bg-muted/30' : 'bg-muted/50'} ${entry.is_locked ? 'opacity-60' : ''}`}
                                                    data-testid={`time-entry-${entry.id}`}
                                                >
                                                    <div className="flex items-center gap-3">
                                                        {entry.is_locked && <Lock className="w-4 h-4 text-muted-foreground" />}
                                                        {entry.is_pause && <Pause className="w-4 h-4 text-muted-foreground" />}
                                                        <div>
                                                            <div className="flex items-center gap-2">
                                                                <span className="font-mono text-sm">
                                                                    {format(new Date(entry.start_time), 'HH:mm')} - {format(new Date(entry.end_time), 'HH:mm')}
                                                                </span>
                                                                {entry.is_edited && (
                                                                    <Badge variant="outline" className="text-xs">Bearbeitet</Badge>
                                                                )}
                                                            </div>
                                                            <p className="text-sm text-muted-foreground flex items-center gap-2">
                                                                {entry.is_pause ? (
                                                                    <span className="flex items-center gap-1">☕ Pause</span>
                                                                ) : (
                                                                    <>
                                                                        {entry.customer_name && (
                                                                            <span className="flex items-center gap-1">
                                                                                🏢 {entry.customer_name}
                                                                            </span>
                                                                        )}
                                                                        {entry.description && entry.description.startsWith('[') && (
                                                                            <Badge variant="secondary" className="text-xs font-normal">
                                                                                {entry.description.includes('Grafik') && '🎨'}
                                                                                {entry.description.includes('Videoschnitt') && '🎬'}
                                                                                {entry.description.includes('Reporting') && '📊'}
                                                                                {entry.description.includes('Storyboard') && '📝'}
                                                                                {entry.description.includes('Entwicklung') && '💻'}
                                                                                {entry.description.includes('Meeting') && '👥'}
                                                                                {entry.description.includes('Planung') && '📅'}
                                                                                {entry.description.includes('Support') && '🛟'}
                                                                                {' '}{entry.description.match(/\[(.*?)\]/)?.[1]}
                                                                            </Badge>
                                                                        )}
                                                                        {entry.description && !entry.description.startsWith('[') && (
                                                                            <span className="truncate max-w-[200px]">{entry.description}</span>
                                                                        )}
                                                                        {entry.description && entry.description.includes(']') && entry.description.split(']')[1]?.trim() && (
                                                                            <span className="truncate max-w-[200px] text-muted-foreground/70">
                                                                                {entry.description.split(']')[1].trim()}
                                                                            </span>
                                                                        )}
                                                                        {!entry.customer_name && !entry.description && (
                                                                            <span className="text-muted-foreground/50">Keine Beschreibung</span>
                                                                        )}
                                                                    </>
                                                                )}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-4">
                                                        <span className="font-medium">{formatDuration(entry.duration_seconds)}</span>
                                                        {!entry.is_locked && (
                                                            <div className="flex gap-1">
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    onClick={() => openEditModal(entry)}
                                                                    data-testid={`edit-entry-${entry.id}`}
                                                                >
                                                                    <Edit className="w-4 h-4" />
                                                                </Button>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    onClick={() => handleDeleteEntry(entry.id)}
                                                                    data-testid={`delete-entry-${entry.id}`}
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                </Button>
                                                            </div>
                                                        )}
                                                        {entry.is_locked && (
                                                            <span className="text-xs text-muted-foreground">🔒 Gesperrt</span>
                                                        )}
                                                    </div>
                                                </motion.div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                        </div>
                    ) : (
                        <div className="text-center py-12 text-muted-foreground">
                            <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                            <p>Keine Einträge in diesem Zeitraum</p>
                            <Button variant="outline" className="mt-4" onClick={() => { resetForm(); setShowCreateModal(true); }}>
                                <Plus className="w-4 h-4 mr-2" />
                                Zeit hinzufügen
                            </Button>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Create/Edit Modal */}
            <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
                <DialogContent data-testid="time-entry-modal">
                    <DialogHeader>
                        <DialogTitle>{editingEntry ? 'Eintrag bearbeiten' : 'Zeit hinzufügen'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        {canManageTeamTime && !editingEntry && (
                            <div>
                                <Label>Für Mitarbeiter</Label>
                                <Select value={entryForUserId || 'me'} onValueChange={(v) => setEntryForUserId(v === 'me' ? '' : v)}>
                                    <SelectTrigger data-testid="entry-for-user"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="me">Ich selbst</SelectItem>
                                        {teamUsers.filter(u => u.id !== user?.id).map(u => (
                                            <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <p className="text-[11px] text-muted-foreground mt-1">Die Zeit landet auf dem Konto der gewählten Person, nicht auf deinem.</p>
                            </div>
                        )}
                        <div>
                            <Label>Datum</Label>
                            <Input
                                type="date"
                                value={formData.date}
                                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                                data-testid="entry-date"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Startzeit</Label>
                                <Input
                                    type="time"
                                    value={formData.start_time}
                                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                                    data-testid="entry-start"
                                />
                            </div>
                            <div>
                                <Label>Endzeit</Label>
                                <Input
                                    type="time"
                                    value={formData.end_time}
                                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                                    data-testid="entry-end"
                                />
                            </div>
                        </div>
                        <div>
                            <Label>Task (optional)</Label>
                            <Select value={formData.task_id || "none"} onValueChange={(v) => setFormData({ ...formData, task_id: v === "none" ? "" : v })}>
                                <SelectTrigger data-testid="entry-task">
                                    <SelectValue placeholder="Task wählen" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">Kein Task</SelectItem>
                                    {tasks.filter(t => !t.is_archived).map(t => (
                                        <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        {projectsEnabled && (
                        <div>
                            <Label>Projekt (optional)</Label>
                            <Select value={formData.project_id || "none"} onValueChange={(v) => setFormData({ ...formData, project_id: v === "none" ? "" : v })}>
                                <SelectTrigger data-testid="entry-project">
                                    <SelectValue placeholder="Projekt wählen" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">Kein Projekt</SelectItem>
                                    {projects.map(p => (
                                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        )}
                        <div>
                            <Label>Kunde *</Label>
                            <div className="mt-1">
                                <CustomerPicker
                                    customers={customers}
                                    value={formData.customer_id}
                                    onChange={(id) => setFormData({ ...formData, customer_id: id })}
                                    placeholder="Kunde wählen..."
                                    testId="entry-customer"
                                />
                            </div>
                        </div>
                        <div>
                            <Label>Beschreibung</Label>
                            <Input
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder="Was wurde gemacht?"
                                data-testid="entry-description"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                id="isPause"
                                checked={formData.is_pause}
                                onChange={(e) => setFormData({ ...formData, is_pause: e.target.checked })}
                                className="rounded border-border"
                                data-testid="entry-pause"
                            />
                            <Label htmlFor="isPause">Als Pause markieren</Label>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setShowCreateModal(false); setEditingEntry(null); }}>
                            Abbrechen
                        </Button>
                        <Button onClick={handleSaveEntry} data-testid="save-entry-button">
                            {editingEntry ? 'Aktualisieren' : 'Erstellen'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            
            {/* Alltime Balance Modal */}
            <Dialog open={showBalanceModal} onOpenChange={setShowBalanceModal}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Clock className="w-5 h-5 text-primary" />
                            Stunden-Saldo Übersicht
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-6 py-4">
                        {/* Main Balance */}
                        <div className="text-center p-6 bg-muted/30 rounded-xl">
                            <p className="text-sm text-muted-foreground mb-2">Gesamt-Saldo (seit Beginn)</p>
                            <div className={`text-5xl font-bold flex items-center justify-center gap-3 ${
                                alltimeBalance?.alltime_balance_hours > 0 ? 'text-emerald-500' : 
                                alltimeBalance?.alltime_balance_hours < 0 ? 'text-red-500' : ''
                            }`}>
                                {alltimeBalance?.alltime_balance_hours > 0 && <TrendingUp className="w-8 h-8" />}
                                {alltimeBalance?.alltime_balance_hours < 0 && <TrendingDown className="w-8 h-8" />}
                                {alltimeBalance?.alltime_balance_hours > 0 ? '+' : ''}{alltimeBalance?.alltime_balance_hours || 0}h
                            </div>
                            <p className="text-sm text-muted-foreground mt-3">
                                {alltimeBalance?.alltime_worked_hours || 0}h gearbeitet / {alltimeBalance?.alltime_expected_hours || 0}h Soll
                            </p>
                        </div>

                        {/* Summary Stats */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="p-4 bg-emerald-500/10 rounded-lg text-center">
                                <p className="text-2xl font-bold text-emerald-600">{alltimeBalance?.alltime_worked_hours || 0}h</p>
                                <p className="text-sm text-muted-foreground">Gesamt gearbeitet</p>
                            </div>
                            <div className="p-4 bg-blue-500/10 rounded-lg text-center">
                                <p className="text-2xl font-bold text-blue-600">{alltimeBalance?.weekly_hours_target || 40}h</p>
                                <p className="text-sm text-muted-foreground">Wochenstunden-Ziel</p>
                            </div>
                        </div>

                        {/* Monthly Breakdown */}
                        {alltimeBalance?.monthly_stats && alltimeBalance.monthly_stats.length > 0 && (
                            <div>
                                <p className="text-sm font-medium mb-3">Monatsübersicht {new Date().getFullYear()}</p>
                                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                                    {alltimeBalance.monthly_stats.map((month) => (
                                        <div key={month.month} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                                            <span className="font-medium">{month.month_name}</span>
                                            <div className="flex items-center gap-4">
                                                <span className="text-sm text-muted-foreground">
                                                    {month.worked_hours}h / {month.expected_hours}h
                                                </span>
                                                <span className={`font-semibold ${
                                                    month.balance > 0 ? 'text-emerald-500' : 
                                                    month.balance < 0 ? 'text-red-500' : ''
                                                }`}>
                                                    {month.balance > 0 ? '+' : ''}{month.balance}h
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <p className="text-xs text-muted-foreground text-center">
                            Überstunden und Minusstunden werden automatisch verrechnet.
                            Ein Minus in einem Monat wird durch Mehrarbeit im nächsten Monat ausgeglichen.
                        </p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowBalanceModal(false)}>
                            Schließen
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Stop Timer Modal - iOS Style */}
            <Dialog open={showStopModal} onOpenChange={setShowStopModal}>
                <DialogContent className="max-w-md p-0 overflow-hidden">
                    {/* Header with gradient */}
                    <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent p-6 pb-4">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-3 text-xl">
                                <div className="w-12 h-12 rounded-2xl bg-primary/20 flex items-center justify-center">
                                    <span className="text-2xl">⏱️</span>
                                </div>
                                <div>
                                    <p className="font-semibold">Zeit stoppen</p>
                                    <p className="text-3xl font-mono font-bold text-primary">{formattedTime}</p>
                                </div>
                            </DialogTitle>
                        </DialogHeader>
                    </div>
                    
                    <div className="p-6 pt-2 space-y-5">
                        {/* Quick Action - Pause */}
                        <div>
                            <button
                                onClick={() => handleStopTimer(true)}
                                className="w-full flex items-center justify-center gap-2 p-4 rounded-2xl bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/20 transition-all hover:scale-[1.02]"
                                data-testid="stop-as-pause"
                            >
                                <span className="text-2xl">☕</span>
                                <span className="font-medium text-amber-700 dark:text-amber-400">Als Pause buchen</span>
                            </button>
                        </div>

                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <span className="w-full border-t border-dashed" />
                            </div>
                            <div className="relative flex justify-center">
                                <span className="bg-background px-3 text-xs text-muted-foreground uppercase tracking-wider">oder buchen auf</span>
                            </div>
                        </div>

                        {/* Customer Dropdown with Search */}
                        <div>
                            <Label className="flex items-center gap-2 text-sm font-medium">
                                <span>🏢</span> Kunde *
                            </Label>
                            <div className="mt-2">
                                <CustomerPicker
                                    customers={customers}
                                    value={stopForm.customer_id}
                                    onChange={(id) => setStopForm({ ...stopForm, customer_id: id })}
                                    placeholder="Kunde auswählen..."
                                    triggerClassName="h-12 rounded-xl"
                                    showAvatar={false}
                                    testId="stop-customer"
                                />
                            </div>
                        </div>

                        {/* Task Dropdown */}
                        <div>
                            <Label className="flex items-center gap-2 text-sm font-medium">
                                <span>📝</span> Task
                            </Label>
                            <Select 
                                value={stopForm.task_id} 
                                onValueChange={(val) => setStopForm({ ...stopForm, task_id: val })}
                            >
                                <SelectTrigger className="mt-2 h-12 rounded-xl">
                                    <SelectValue placeholder="Task auswählen (optional)..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {tasks.filter(t => t.status !== 'done' && !t.is_archived).map(t => (
                                        <SelectItem key={t.id} value={t.id}>
                                            <span className="font-medium">{t.title}</span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Description */}
                        <div>
                            <Label className="flex items-center gap-2 text-sm font-medium">
                                <span>💬</span> Beschreibung
                            </Label>
                            <Input
                                className="mt-2 h-12 rounded-xl"
                                placeholder="Was wurde gemacht? (optional)"
                                value={stopForm.description}
                                onChange={(e) => setStopForm({ ...stopForm, description: e.target.value })}
                            />
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-3 pt-2">
                            <Button 
                                variant="outline" 
                                className="flex-1 h-12 rounded-xl"
                                onClick={() => setShowStopModal(false)}
                            >
                                Abbrechen
                            </Button>
                            <Button 
                                className="flex-1 h-12 rounded-xl"
                                onClick={() => handleStopTimer(false)}
                                disabled={!stopForm.customer_id}
                                data-testid="stop-timer-confirm"
                            >
                                Zeit speichern
                            </Button>
                        </div>

                        {/* Cancel Timer Option */}
                        <button
                            onClick={() => {
                                cancelTracking();
                                setShowStopModal(false);
                                toast.info('Timer abgebrochen');
                            }}
                            className="w-full text-center text-sm text-muted-foreground hover:text-destructive transition-colors"
                        >
                            Timer ohne Speichern abbrechen
                        </button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Export Modal */}
            <Dialog open={showExportModal} onOpenChange={setShowExportModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FileSpreadsheet className="w-5 h-5 text-green-500" />
                            Zeiteinträge exportieren
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <p className="text-sm text-muted-foreground">
                            Wähle den Zeitraum für den Export. Die Datei wird als CSV heruntergeladen und kann in Excel oder Google Sheets geöffnet werden.
                        </p>
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Startdatum</Label>
                                <Input
                                    type="date"
                                    value={exportRange.start}
                                    onChange={(e) => setExportRange(prev => ({ ...prev, start: e.target.value }))}
                                    data-testid="export-start-date"
                                />
                            </div>
                            <div>
                                <Label>Enddatum</Label>
                                <Input
                                    type="date"
                                    value={exportRange.end}
                                    onChange={(e) => setExportRange(prev => ({ ...prev, end: e.target.value }))}
                                    data-testid="export-end-date"
                                />
                            </div>
                        </div>
                        
                        <div className="p-3 bg-muted/50 rounded-lg text-sm">
                            <p className="font-medium mb-1">Exportierte Felder:</p>
                            <p className="text-muted-foreground">
                                Datum, Kunde, Projekt, Task, Beschreibung, Aktivitätstyp, Dauer (Stunden), Dauer (HH:MM), Mitarbeiter, Notizen
                            </p>
                        </div>
                        
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Download className="w-4 h-4" />
                            <span>Dateiname: zeiteintraege_{exportRange.start}_bis_{exportRange.end}.csv</span>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowExportModal(false)}>
                            Abbrechen
                        </Button>
                        <Button onClick={handleExport} disabled={exporting} className="bg-green-600 hover:bg-green-700" data-testid="export-button">
                            {exporting ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                    Exportiere...
                                </>
                            ) : (
                                <>
                                    <Download className="w-4 h-4 mr-2" />
                                    CSV herunterladen
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default TimePage;
